import { useAppSelector } from '../../config/hooks';
import { getProfile } from '../../redux/selectors/profileSelector';
import {
  CalculatePriceRequestT,
  useGetOpenCalculatePriceQuery,
  useGetProfileCalculatePriceQuery,
} from '../api/form/open/shopping-cart.api';
import { useMemo, useState } from 'react';
import { getBasketReducer } from '../../redux/selectors/orderFormSelector';
import { getSummaryReducer } from '../../redux/selectors/summaryReducer.selectors';
import { useDebounce } from 'react-use';
import { BasketItemT } from '../../@types/basketItem.type';
import { getLoyaltyPoints } from '../../redux/selectors/basketSelector';
import { PaymentTypeE } from '../../@types/common.enum';
import {
  generateCustomDeliveryMeals,
  generateSideOrdersList,
  mapDeliveryDates,
  mapSelectedMeals,
} from '../../components/pages/StepSummary/utils/generateOrderData.util';
import { generateSelectedFees } from '../../components/pages/StepSummary/utils/generateSelectedFees.util';
import { getServerConfig } from '../../features/general/generalSlice';
import useGenerateCalculation from './useGenerateCalculation.hook';

const useCalculatePrice = () => {
  const { companyId } = useAppSelector(getServerConfig) || {};

  const isPricingEnabled = true;

  const { profile } = useAppSelector(getProfile) || {};

  const [data, setData] = useState<CalculatePriceRequestT | null>(null);

  const {
    additionalFees: { selectedFees },
    basket,
    promoCode,
  } = useAppSelector(getBasketReducer) || {};

  const { newAddressDetails, pickupPointId } =
    useAppSelector(getSummaryReducer) || {};

  const { usedLoyaltyPoints } = useAppSelector(getLoyaltyPoints) || 0;

  const promoCodes = useMemo(
    () => [promoCode?.code ? promoCode.code.code : ''],
    [promoCode.code]
  );

  const simpleOrders = useMemo(() => {
    return basket.flatMap((item: BasketItemT, index: number) => {
      const {
        mealExchange,
        orderId,
        selectedCalories,
        selectedDays,
        selectedMeals,
        selectedProgram,
        selectedSideOrders,
        sets,
        testOrder,
      } = item;

      const data = Array.from({ length: sets }, () => ({
        itemId: orderId,
        deliveryDates: mapDeliveryDates(selectedDays),
        customDeliveryMeals: generateCustomDeliveryMeals(
          selectedProgram,
          // TODO ANY
          mealExchange.days as any
        ),
        deliveryMeals: mapSelectedMeals(selectedMeals),
        dietCaloriesId: selectedCalories.dietCaloriesId,
        paymentType: PaymentTypeE.ONLINE,
        pickupPointId,
        sideOrders: generateSideOrdersList(
          // TODO ANY
          mealExchange.days as any,
          selectedSideOrders,
          selectedDays
        ),
        testOrder,
      }));

      // this is only added to the first diet
      if (index === 0) {
        generateSelectedFees(selectedFees, data);
      }

      return data;
    });
  }, [basket, pickupPointId, selectedFees]);

  const calculation = useGenerateCalculation();

  const orderData = useMemo(
    () => ({
      promoCodes,
      simpleOrders,
      loyaltyProgramPoints: usedLoyaltyPoints,
      // 0 because u can't use in this order form
      loyaltyProgramPointsGlobal: 0,
      companyId,
      calculation,
      cityId: newAddressDetails.city.cityId,
    }),
    [
      calculation,
      companyId,
      newAddressDetails.city.cityId,
      promoCodes,
      simpleOrders,
      usedLoyaltyPoints,
    ]
  );

  useDebounce(
    () => {
      setData(orderData);
    },
    500,
    [orderData]
  );

  const isSkip =
    !data ||
    data?.simpleOrders?.length === 0 ||
    !isPricingEnabled ||
    !newAddressDetails.city.cityId;

  const {
    data: openData,
    isFetching: openIsFetching,
    isUninitialized: openIsUninitialized,
  } = useGetOpenCalculatePriceQuery(data as CalculatePriceRequestT, {
    skip: profile?.email || isSkip,
  });

  const {
    data: profileData,
    isFetching: profileIsFetching,
    isUninitialized: profileIsUninitialized,
  } = useGetProfileCalculatePriceQuery(data as CalculatePriceRequestT, {
    skip: !profile?.email || isSkip,
  });

  const isAnyUninitialized = profile?.email
    ? profileIsUninitialized
    : openIsUninitialized;

  const isFetching = openIsFetching || profileIsFetching || isAnyUninitialized;

  const prices = profileData ?? openData;

  const {
    cart: {
      totalCostWithoutDiscounts = 0,
      totalDeliveriesOnDateDiscount = 0,
      totalOrderLengthDiscount = 0,
    } = {},
  } = prices || {};

  return {
    prices,
    isPricingEnabled,
    isFetching: isPricingEnabled && isFetching,
    finalSum:
      totalCostWithoutDiscounts -
      totalDeliveriesOnDateDiscount -
      totalOrderLengthDiscount,
  };
};

export default useCalculatePrice;
